/**
 * Blessac product page — gallery interaction, generic variant-option matching,
 * and preorder line-property rewriting on variant change.
 *
 * Add-to-cart submission itself is NOT reimplemented here: the section markup
 * wraps its forms in Horizon's own <product-form-component> /
 * <add-to-cart-component> (see assets/product-form.js), so fetch, error
 * handling, and the CartLinesUpdateEvent dispatch that refreshes the cart
 * drawer/bubble are Horizon's native code, unmodified. This module only
 * owns: gallery (stage/thumbs/lightbox/mobile swipe), matching the selected
 * option values to a variant, and syncing price/label/preorder-property DOM
 * from the variant JSON embedded by the section.
 *
 * Idempotent: re-running init() on an already-initialized root is a no-op.
 * Cleans up its own document/window listeners via AbortController on
 * 'shopify:section:unload'.
 */

import { CartLinesUpdateEvent } from '@shopify/events';

const READY_ATTR = 'data-blessac-pdp-ready';

const moneyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD',
});

/** @param {number} cents */
function formatCents(cents) {
  return moneyFormatter.format(cents / 100);
}

/**
 * @param {Element} root
 * @param {string} selector
 * @param {string} text
 */
function setText(root, selector, text) {
  const el = root.querySelector(selector);
  if (el) el.textContent = text;
}

/**
 * @param {Element} root
 * @param {AbortSignal} signal
 */
function initGallery(root, signal) {
  const stageImg = root.querySelector('[data-pdp-stageimg]');
  const scount = root.querySelector('[data-pdp-scount]');
  const thumbsWrap = root.querySelector('[data-pdp-thumbs]');
  const thumbs = Array.from(root.querySelectorAll('[data-pdp-thumb]'));
  const track = root.querySelector('[data-pdp-track]');
  const swcount = root.querySelector('[data-pdp-swcount]');
  const dots = Array.from(root.querySelectorAll('[data-pdp-dots] i'));
  const stageBtn = root.querySelector('[data-pdp-stage]');
  const lb = /** @type {HTMLDialogElement | null} */ (root.querySelector('[data-pdp-lightbox]'));

  if (thumbs.length === 0) return;

  const media = thumbs.map((btn) => ({
    full: btn.getAttribute('data-full-src') || '',
    alt: btn.getAttribute('data-full-alt') || '',
  }));

  let current = 0;

  function show(index) {
    current = ((index % media.length) + media.length) % media.length;
    const entry = media[current];

    if (stageImg && entry) {
      stageImg.setAttribute('src', entry.full);
      stageImg.setAttribute('alt', entry.alt);
    }

    if (scount) scount.textContent = `${current + 1} / ${media.length}`;

    for (const btn of thumbs) {
      btn.setAttribute('aria-current', String(Number(btn.getAttribute('data-index')) === current));
    }

    if (lb && lb.open) paintLightbox();
  }

  function paintLightbox() {
    if (!lb) return;
    const img = lb.querySelector('[data-pdp-lb-img]');
    const cnt = lb.querySelector('[data-pdp-lb-cnt]');
    const entry = media[current];
    if (img && entry) {
      img.setAttribute('src', entry.full);
      img.setAttribute('alt', entry.alt);
    }
    if (cnt) cnt.textContent = `${current + 1} / ${media.length}`;
  }

  function openLightbox() {
    if (!lb) return;
    paintLightbox();
    if (typeof lb.showModal === 'function') lb.showModal();
  }

  thumbsWrap?.addEventListener(
    'click',
    (event) => {
      const btn = /** @type {HTMLElement | null} */ (event.target instanceof Element ? event.target.closest('[data-pdp-thumb]') : null);
      if (!btn) return;
      show(Number(btn.getAttribute('data-index')));
    },
    { signal }
  );

  if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    thumbsWrap?.addEventListener(
      'mouseover',
      (event) => {
        const btn = /** @type {HTMLElement | null} */ (event.target instanceof Element ? event.target.closest('[data-pdp-thumb]') : null);
        if (!btn) return;
        const index = Number(btn.getAttribute('data-index'));
        if (index !== current) show(index);
      },
      { signal }
    );
  }

  stageBtn?.addEventListener('click', openLightbox, { signal });

  lb?.addEventListener('click', (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.matches('[data-pdp-lb-close]') || target === lb) lb.close();
    if (target.matches('[data-pdp-lb-prev]')) show(current - 1);
    if (target.matches('[data-pdp-lb-next]')) show(current + 1);
  }, { signal });

  document.addEventListener(
    'keydown',
    (event) => {
      if (!lb?.open) return;
      if (event.key === 'ArrowLeft') show(current - 1);
      if (event.key === 'ArrowRight') show(current + 1);
    },
    { signal, capture: true }
  );

  function syncRail() {
    if (!thumbsWrap) return;
    const hidden = thumbsWrap.scrollHeight - thumbsWrap.clientHeight;
    const atEnd = thumbsWrap.scrollTop >= hidden - 2;
    thumbsWrap.classList.toggle('is-more', hidden > 2 && !atEnd);
  }

  thumbsWrap?.addEventListener('scroll', syncRail, { passive: true, signal });
  window.addEventListener('resize', syncRail, { signal });
  syncRail();

  // ---- mobile swipe track: position read from scrollLeft, not scroll-event
  // count, since a flung swipe can cross more than one frame at a time.
  if (track) {
    function syncTrack() {
      if (!track) return;
      const figures = Array.from(track.children);
      let best = 0;
      let bestDelta = Infinity;
      figures.forEach((fig, i) => {
        const delta = Math.abs(/** @type {HTMLElement} */ (fig).offsetLeft - track.scrollLeft);
        if (delta < bestDelta) {
          bestDelta = delta;
          best = i;
        }
      });
      if (swcount) swcount.textContent = `${best + 1}/${media.length}`;
      dots.forEach((dot, i) => dot.classList.toggle('is-on', i === best));
      current = best;
    }

    let settleTimeout;
    let rafId;
    track.addEventListener(
      'scroll',
      () => {
        if (rafId) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(syncTrack);
        clearTimeout(settleTimeout);
        settleTimeout = setTimeout(syncTrack, 140);
      },
      { passive: true, signal }
    );

    if ('onscrollend' in window) track.addEventListener('scrollend', syncTrack, { signal });
  }
}

/**
 * @param {Element} root
 * @param {AbortSignal} signal
 */
function initVariants(root, signal) {
  const dataEl = root.querySelector('[data-blessac-pdp-variants]');
  if (!dataEl?.textContent) return null;

  /** @type {{discountEnabled: boolean, variants: Array<Record<string, any>>}} */
  let payload;
  try {
    payload = JSON.parse(dataEl.textContent);
  } catch {
    return null;
  }

  const variants = Array.isArray(payload.variants) ? payload.variants : [];
  if (variants.length === 0) return null;

  function findVariant() {
    const inputs = Array.from(root.querySelectorAll('[data-pdp-option-input]:checked'));
    if (inputs.length === 0) return variants[0];

    const byPosition = new Map();
    for (const input of inputs) {
      const position = Number(input.getAttribute('data-position'));
      byPosition.set(position, input.value);
    }
    const positions = Array.from(byPosition.keys()).sort((a, b) => a - b);
    const selected = positions.map((p) => byPosition.get(p));

    return (
      variants.find((v) => Array.isArray(v.options) && v.options.length === selected.length && v.options.every((o, i) => o === selected[i])) ??
      null
    );
  }

  /** @param {Element} form @param {string} name @param {string} marker @param {string | null} value */
  function setHiddenInput(form, name, marker, value) {
    let input = form.querySelector(`[data-pdp-prop="${marker}"]`);
    if (value == null || value === '') {
      input?.remove();
      return;
    }
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.setAttribute('data-pdp-prop', marker);
      form.appendChild(input);
    }
    /** @type {HTMLInputElement} */ (input).value = value;
  }

  const state = { variant: variants[0] };

  /**
   * Sum of the checked Your Set companions (list and after-code cents),
   * plus how many there are and whether every one is code-eligible — the
   * total line only promises "the code covers every piece" when it's true.
   */
  function extrasCents() {
    let list = 0;
    let now = 0;
    let count = 0;
    let allEligible = true;
    root.querySelectorAll('[data-pdp-set-check]:checked').forEach((cb) => {
      list += Number(cb.getAttribute('data-price-cents')) || 0;
      now += Number(cb.getAttribute('data-now-cents')) || 0;
      count += 1;
      if (cb.getAttribute('data-elig') !== 'true') allEligible = false;
    });
    return { list, now, count, allEligible };
  }

  /**
   * Every money figure on the page derives from ONE total: the selected
   * variant plus the checked set companions (mockup rule — no number may
   * stand still while the others move).
   */
  function renderMoney() {
    const v = state.variant;
    if (!v || typeof v.priceCents !== 'number') return;
    const extras = extrasCents();
    // List price is the compare-at markdown when the variant carries one,
    // otherwise the plain price (which then also covers the checkout-code
    // discount path, where nowCents < priceCents).
    const listBase = typeof v.compareCents === 'number' && v.compareCents > v.priceCents ? v.compareCents : v.priceCents;
    const list = listBase + extras.list;
    const now = v.nowCents + extras.now;
    const save = list - now;

    setText(root, '[data-pdp-now]', formatCents(now));
    setText(root, '[data-pdp-was]', formatCents(list));
    setText(root, '[data-pdp-save]', `Save ${formatCents(save)}`);
    // Struck "was" and "Save" only make sense while the list sits above now;
    // hide them on a full-price variant so switching packs stays honest.
    const wasEl = /** @type {HTMLElement | null} */ (root.querySelector('[data-pdp-was]'));
    if (wasEl) wasEl.hidden = !(list > now);
    const saveEl = /** @type {HTMLElement | null} */ (root.querySelector('[data-pdp-save]'));
    if (saveEl) saveEl.hidden = !(list > now);
    setText(root, '[data-pdp-installment]', formatCents(Math.floor(now / 4)));
    setText(root, '[data-pdp-ctatotal]', formatCents(now));
    setText(root, '[data-pdp-saveamt]', formatCents(save));
    setText(root, '[data-pdp-paynow]', formatCents(now));
    setText(root, '[data-pdp-sticky-total]', formatCents(now));

    // Anchor row mirrors the buy box: after-code price, list struck beside it
    setText(root, '[data-pdp-set-baseprice]', v.nowMoney);
    const baseWas = /** @type {HTMLElement | null} */ (root.querySelector('[data-pdp-set-basewas]'));
    if (baseWas) {
      baseWas.textContent = v.priceMoney;
      baseWas.hidden = v.nowCents === v.priceCents;
    }

    // Total line under the set (afroyla __total): only once the set is
    // actually a set, and only promising the code when every piece qualifies
    const totalEl = /** @type {HTMLElement | null} */ (root.querySelector('[data-pdp-set-total]'));
    if (totalEl) {
      const pieces = extras.count + 1;
      if (extras.count > 0) {
        totalEl.textContent = '';
        totalEl.appendChild(document.createTextNode(`Your set — ${pieces} pieces, `));
        const strong = document.createElement('strong');
        strong.textContent = formatCents(now);
        totalEl.appendChild(strong);
        const anchorEligible = payload.discountEnabled && v.nowCents < v.priceCents;
        totalEl.appendChild(document.createTextNode(anchorEligible && extras.allEligible ? '. The code covers every piece.' : '.'));
        totalEl.hidden = false;
      } else {
        totalEl.hidden = true;
      }
    }
  }

  function applyVariant(variant) {
    if (!variant) return;
    state.variant = variant;

    renderMoney();
    setText(root, '[data-pdp-ctalabel]', variant.label);
    setText(root, '[data-pdp-sticky-label]', variant.label);
    root.querySelectorAll('[data-pdp-sticky-label]').forEach((btn) => {
      btn.disabled = Boolean(variant.disabled);
    });

    root.querySelectorAll('[data-pdp-variant-input]').forEach((input) => {
      /** @type {HTMLInputElement} */ (input).value = String(variant.id);
    });

    const ctaBtn = /** @type {HTMLButtonElement | null} */ (root.querySelector('[data-pdp-ctabtn]'));
    if (ctaBtn) ctaBtn.disabled = Boolean(variant.disabled);

    root.querySelectorAll('[data-pdp-badge]').forEach((badge) => {
      /** @type {HTMLElement} */ (badge).hidden = !variant.preorder;
    });

    const preAssure = root.querySelector('[data-pdp-assure-preorder]');
    const stdAssure = root.querySelector('[data-pdp-assure-standard]');
    if (preAssure) /** @type {HTMLElement} */ (preAssure).hidden = !variant.preorder;
    if (stdAssure) /** @type {HTMLElement} */ (stdAssure).hidden = Boolean(variant.preorder);

    root.querySelectorAll('[data-pdp-batch-for]').forEach((panel) => {
      /** @type {HTMLElement} */ (panel).hidden = panel.getAttribute('data-pdp-batch-for') !== String(variant.id);
    });

    root.querySelectorAll('[data-type="add-to-cart-form"], [data-type="buy-now-form"]').forEach((form) => {
      const want = Boolean(variant.preorder);
      setHiddenInput(form, 'properties[_preorder]', 'preorder', want ? 'true' : null);
      setHiddenInput(form, 'properties[_preorder_batch]', 'batch', want ? variant.batchCode : null);
      setHiddenInput(form, 'properties[_preorder_eta]', 'eta', want && variant.etaText ? variant.etaText : null);
    });
  }

  state.variant = findVariant() ?? variants[0];

  root.addEventListener(
    'change',
    (event) => {
      if (!(event.target instanceof Element) || !event.target.matches('[data-pdp-option-input]')) return;
      applyVariant(findVariant());
    },
    { signal }
  );

  return { renderMoney, getVariant: () => state.variant };
}

/**
 * Your Set builder: checked companions ride along with the main add-to-cart.
 * The base product goes through Horizon's untouched product-form flow (all
 * phase-5 property guarantees intact); once that add settles, the checked
 * companions are added in one `/cart/add.js` items[] call — each carrying its
 * OWN preorder line properties from Liquid — and a CartLinesUpdateEvent with
 * the rendered sections is dispatched so the drawer and bubble repaint through
 * Horizon's normal machinery. The 'blessac-set' context guards the listener
 * from chaining on its own event.
 * @param {Element} root
 * @param {AbortSignal} signal
 * @param {{renderMoney: () => void, getVariant: () => Record<string, any>} | null} variantApi
 */
function initSetBuilder(root, signal, variantApi) {
  const set = root.querySelector('[data-pdp-set]');
  if (!set) return;

  set.addEventListener(
    'change',
    (event) => {
      const cb = event.target instanceof Element ? event.target.closest('[data-pdp-set-check]') : null;
      if (!cb) return;
      cb.closest('.blessac-pdp__setrow')?.classList.toggle('is-on', /** @type {HTMLInputElement} */ (cb).checked);
      variantApi?.renderMoney();
    },
    { signal }
  );

  const moreBtn = set.querySelector('[data-pdp-set-more]');
  moreBtn?.addEventListener(
    'click',
    () => {
      set.querySelectorAll('.blessac-pdp__setrow.is-more').forEach((row) => {
        /** @type {HTMLElement} */ (row).hidden = false;
      });
      /** @type {HTMLElement} */ (moreBtn).hidden = true;
    },
    { signal }
  );

  // ---- Size & photos quick view (afroyla set-picker QV, on a native dialog) ----
  const qv = /** @type {HTMLDialogElement | null} */ (set.querySelector('[data-pdp-qv]'));
  const qvSlot = qv?.querySelector('[data-pdp-qv-slot]');
  const qvAdd = /** @type {HTMLButtonElement | null} */ (qv?.querySelector('[data-pdp-qv-add]') ?? null);
  let qvIdx = /** @type {string | null} */ (null);

  function qvInput(idx) {
    return /** @type {HTMLInputElement | null} */ (
      set.querySelector(`[data-pdp-qv-open="${idx}"]`)?.closest('.blessac-pdp__setrow')?.querySelector('[data-pdp-set-check]') ?? null
    );
  }

  /**
   * Ships line: the member's own pre-order window when it has one, otherwise
   * "Now" — with a note that the anchor follows later when THAT one is the
   * pre-order piece.
   */
  function qvShipsText(input) {
    const memberPre = input?.getAttribute('data-pre') === 'true';
    const memberEta = input?.getAttribute('data-eta') || '';
    if (memberPre) return memberEta ? `${memberEta} (pre-order)` : 'With the next batch (pre-order)';
    const anchor = variantApi?.getVariant();
    const anchorShort = set.getAttribute('data-anchor-short') || 'piece';
    if (anchor?.preorder) return `Now — your ${anchorShort} follows${anchor.etaText ? ` ${anchor.etaText}` : ''}`;
    return 'Now';
  }

  function paintQvAdd() {
    if (!qvAdd || qvIdx === null) return;
    const input = qvInput(qvIdx);
    if (input?.checked) {
      qvAdd.textContent = 'Added ✓';
      qvAdd.classList.add('is-added');
    } else {
      const nowCents = Number(input?.getAttribute('data-now-cents')) || 0;
      qvAdd.textContent = `Add — ${formatCents(nowCents)}`;
      qvAdd.classList.remove('is-added');
    }
  }

  function openQv(idx) {
    const tpl = /** @type {HTMLTemplateElement | null} */ (set.querySelector(`[data-pdp-qv-tpl="${idx}"]`));
    if (!qv || !qvSlot || !tpl) return;
    qvIdx = idx;
    qvSlot.textContent = '';
    qvSlot.appendChild(tpl.content.cloneNode(true));
    const ships = qvSlot.querySelector('[data-pdp-qv-ships]');
    if (ships) ships.textContent = qvShipsText(qvInput(idx));
    paintQvAdd();
    qv.showModal();
  }

  function closeQv() {
    qvIdx = null;
    qv?.close();
  }

  set.addEventListener(
    'click',
    (event) => {
      const opener = event.target instanceof Element ? event.target.closest('[data-pdp-qv-open]') : null;
      if (!opener) return;
      // The opener sits inside the row <label>; stop it from toggling the box
      event.preventDefault();
      event.stopPropagation();
      openQv(opener.getAttribute('data-pdp-qv-open'));
    },
    { signal }
  );

  if (qv) {
    qv.addEventListener(
      'click',
      (event) => {
        // Native dialog: a click on the element itself (not its children) is
        // the backdrop; ✕ carries the close marker explicitly
        if (event.target === qv || (event.target instanceof Element && event.target.closest('[data-pdp-qv-close]'))) closeQv();
      },
      { signal }
    );
    qv.addEventListener('close', () => { qvIdx = null; }, { signal });
  }

  qvAdd?.addEventListener(
    'click',
    () => {
      if (qvIdx === null) return closeQv();
      const input = qvInput(qvIdx);
      if (input && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        paintQvAdd();
        setTimeout(closeQv, 450);
      } else {
        closeQv();
      }
    },
    { signal }
  );

  document.addEventListener(
    'shopify:cart:lines-update',
    (event) => {
      const ev = /** @type {any} */ (event);
      if (ev.action !== 'add' || ev.context === 'blessac-set') return;
      if (!(event.target instanceof Element) || !root.contains(event.target)) return;

      const extras = Array.from(set.querySelectorAll('[data-pdp-set-check]:checked'));
      if (extras.length === 0) return;

      ev.promise
        ?.then((result) => {
          if (result?.detail?.didError) return;
          addExtras(extras);
        })
        .catch(() => {});
    },
    { signal }
  );

  /** @param {Element[]} extras */
  function addExtras(extras) {
    const items = extras.map((cb) => {
      let properties = {};
      try {
        properties = JSON.parse(cb.getAttribute('data-props') || '{}');
      } catch {
        properties = {};
      }
      return { id: Number(/** @type {HTMLInputElement} */ (cb).value), quantity: 1, properties };
    });

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
      .filter(Boolean);

    const formComponent = root.querySelector('product-form-component');
    const deferred = CartLinesUpdateEvent.createPromise();

    formComponent?.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'blessac-set',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: sectionIds.join(',') }),
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          deferred.reject(new Error(response.message || 'Set add failed'));
          return;
        }
        const ajaxCart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: {
            items: ajaxCart.items,
            source: 'product-form-component',
            sourceId: formComponent?.id?.toString() || '',
            itemCount: items.length,
            productId: /** @type {HTMLElement | undefined} */ (formComponent)?.dataset.productId,
            sections: response.sections,
            didError: false,
          },
        });
      })
      .catch((error) => deferred.reject(error));
  }
}

/**
 * In-flight feedback: Horizon's product-form dispatches
 * 'shopify:cart:lines-update' (action "add") when the request STARTS and
 * settles its .promise when the cart responds. Mark the CTA busy in between
 * so the spinner shows while the add is actually running.
 * @param {Element} root
 * @param {AbortSignal} signal
 */
function initAddInFlight(root, signal) {
  document.addEventListener(
    'shopify:cart:lines-update',
    (event) => {
      const detail = /** @type {any} */ (event);
      if (detail.action !== 'add') return;
      if (!(event.target instanceof Element) || !root.contains(event.target)) return;
      const btn = root.querySelector('[data-pdp-ctabtn]');
      if (!btn) return;
      btn.setAttribute('data-adding', '');
      const done = () => btn.removeAttribute('data-adding');
      if (detail.promise?.finally) {
        detail.promise.finally(done);
      } else {
        setTimeout(done, 4000);
      }
    },
    { signal }
  );
}

function init() {
  document.querySelectorAll(`[data-blessac-pdp]:not([${READY_ATTR}])`).forEach((root) => {
    root.setAttribute(READY_ATTR, 'true');
    const controller = new AbortController();
    /** @type {any} */ (root)._blessacPdpAbort = controller;
    initGallery(root, controller.signal);
    const variantApi = initVariants(root, controller.signal);
    initSetBuilder(root, controller.signal, variantApi);
    initAddInFlight(root, controller.signal);
  });
}

function teardown() {
  document.querySelectorAll(`[data-blessac-pdp][${READY_ATTR}]`).forEach((root) => {
    /** @type {any} */ (root)._blessacPdpAbort?.abort();
    root.removeAttribute(READY_ATTR);
  });
}

document.addEventListener('DOMContentLoaded', init);
document.addEventListener('shopify:section:load', init);
document.addEventListener('shopify:section:unload', teardown);
if (document.readyState !== 'loading') init();

function initSticky(root) {
  const sticky = root.querySelector('[data-pdp-sticky]');
  const cta = root.querySelector('[data-pdp-ctabtn]');
  if (!sticky || !cta || sticky.dataset.bound) return;
  sticky.dataset.bound = '1';
  const io = new IntersectionObserver((entries) => {
    const visible = entries[0].isIntersecting;
    sticky.classList.toggle('on', !visible);
    sticky.setAttribute('aria-hidden', String(visible));
    if ('inert' in sticky) sticky.inert = visible;
  }, { rootMargin: '0px 0px -60px 0px' });
  io.observe(cta);
  document.addEventListener('shopify:section:unload', (e) => {
    if (e.target?.contains?.(sticky)) io.disconnect();
  }, { once: true });
}

document.querySelectorAll('[data-blessac-pdp]').forEach(initSticky);
document.addEventListener('shopify:section:load', () => {
  document.querySelectorAll('[data-blessac-pdp]').forEach(initSticky);
});
